/**
 * `<product-lightbox>` (snippets/product-lightbox.liquid): the product gallery's full view.
 *
 * The markup does the work natively: the gallery's buttons open the dialog, the row scrolls and
 * snaps, each slide links to its neighbors, zoom is a checkbox. This adds only what has no native
 * form:
 * - It opens at the photo that was clicked (natively it opens where it was left).
 * - Zooming keeps the point that was clicked under the pointer (natively the top left corner).
 * - A mouse drags the zoomed photo (touch and trackpads scroll it natively).
 * - The arrow keys go to the previous and next photo, and move a zoomed photo.
 * - Previous/next scroll the row without leaving a fragment in the address and zoom out the
 *   photo that is left; the full view always opens zoomed out.
 * - Only the photo in view can take focus or be read out: the other slides are inert. Focus
 *   follows to the photo that comes into view, whose name ("Photo 2 of 4") is then read out.
 */

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const DRAG_START = 4;
const SETTLE = 120;

class ProductLightbox extends HTMLElement {
  #abort = null;
  #point = null;
  #drag = null;
  #dragged = false;
  #settle = 0;

  connectedCallback() {
    this.dialog = this.closest("dialog");
    const gallery = this.closest("product-gallery");
    if (!this.dialog || !gallery) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    gallery.addEventListener("click", (event) => this.#onOpen(event), { signal });
    this.addEventListener("click", (event) => this.#onClick(event), { capture: true, signal });
    this.addEventListener("change", (event) => this.#onZoom(event), { signal });
    this.addEventListener("pointerdown", (event) => this.#onPointerDown(event), { signal });
    this.addEventListener("pointermove", (event) => this.#onPointerMove(event), { signal });
    this.addEventListener("pointerup", () => this.#endDrag(), { signal });
    this.addEventListener("pointercancel", () => this.#endDrag(), { signal });
    // A mouse would otherwise pick the photo up as a file to drag out of the window.
    this.addEventListener("dragstart", (event) => event.preventDefault(), { signal });
    this.dialog.addEventListener("keydown", (event) => this.#onKey(event), { signal });
    for (const track of this.querySelectorAll("[data-lightbox-track]")) {
      track.addEventListener("scroll", () => this.#onScroll(track), { passive: true, signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#settle);
  }

  /** The set that shows: the gallery's Studio/Action choice, else the only one. */
  get #track() {
    const mode = this.closest("product-gallery")?.querySelector(".product-gallery__modes input:checked")?.value;
    return (
      (mode && this.querySelector(`[data-lightbox-track][data-mode="${mode}"]`)) ||
      this.querySelector("[data-lightbox-track]")
    );
  }

  #onOpen(event) {
    const button = event.target.closest("[data-lightbox-open]");
    if (!button) return;
    const index = Number(button.dataset.lightboxOpen);
    // Zoomed out on every open, not on close: the photo would snap back while the dialog fades.
    this.#zoomOut();
    // The button opens the dialog itself (commandfor), right after this click's listeners: wait
    // for that, then show the clicked photo before the first frame paints.
    requestAnimationFrame(() => {
      if (!this.dialog.open) this.dialog.showModal();
      const track = this.#track;
      const slide = track?.children[index];
      if (!slide) return;
      // scrollTo, not scrollIntoView: Firefox brings a reopened row back to where it was left
      // unless a script scrolls it, and scrollIntoView does nothing when the photo is the first.
      track.scrollTo({ left: slide.offsetLeft - track.children[0].offsetLeft, behavior: "instant" });
      this.#show(slide);
    });
  }

  /**
   * Only `slide` is live; its neighbors can't take focus and aren't read out while out of view.
   * Focus that sits in a neighbor moves along first (inert would drop it to the page behind): to
   * the same previous/next link, else the zoom, else whatever the slide has.
   */
  #show(slide) {
    // A rest timer from the scroll before would put the old photo back.
    clearTimeout(this.#settle);
    slide.inert = false;
    const active = document.activeElement;
    const from = active?.closest("[data-lightbox-slide]");
    if (from && from !== slide && this.contains(from)) {
      const nav = active.dataset.lightboxNav;
      const target =
        (nav ? slide.querySelector(`[data-lightbox-nav="${nav}"]`) : slide.querySelector("[data-lightbox-zoom]")) ??
        slide.querySelector("[data-lightbox-nav]") ??
        this.querySelector("[command='close']");
      target?.focus({ preventScroll: true });
    }
    for (const other of slide.parentElement.children) other.inert = other !== slide;
  }

  /** A swipe: once the row rests, the photo it rests on is the live one. */
  #onScroll(track) {
    clearTimeout(this.#settle);
    this.#settle = setTimeout(() => {
      const slide = track.children[Math.round(Math.abs(track.scrollLeft) / track.clientWidth)];
      if (slide) this.#show(slide);
    }, SETTLE);
  }

  #onClick(event) {
    // The click that ends a drag must not reach the zoom checkbox. WebKit sends none after a
    // drag, so the flag may be left over: a click from the keyboard (detail 0) is never that one.
    if (this.#dragged) {
      this.#dragged = false;
      if (event.detail > 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
    const link = event.target.closest("[data-lightbox-nav]");
    if (!link) return;
    const slide = document.getElementById(link.hash.slice(1));
    if (!slide) return;
    event.preventDefault();
    this.#zoomOut();
    this.#show(slide);
    slide.scrollIntoView({ inline: "start", block: "nearest", behavior: reducedMotion.matches ? "instant" : "smooth" });
  }

  #onPointerDown(event) {
    this.#dragged = false;
    // Only a press on the photo gives the zoom its point; from the zoom button it is the middle.
    this.#point = null;
    const pan = event.target.closest("[data-lightbox-pan]");
    if (!pan || event.target.closest(".product-lightbox__zoom-button")) return;
    // Where on the photo the pointer is, as fractions of its size: zooming keeps that point.
    const rect = pan.querySelector("img").getBoundingClientRect();
    this.#point = {
      x: event.clientX,
      y: event.clientY,
      fx: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      fy: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
    const zoomed = pan.querySelector("[data-lightbox-zoom]")?.checked;
    if (zoomed && event.pointerType === "mouse" && event.button === 0) {
      this.#drag = { pan, id: event.pointerId, x: event.clientX, y: event.clientY, left: pan.scrollLeft, top: pan.scrollTop, moving: false };
    }
  }

  #onPointerMove(event) {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moving) {
      if (Math.hypot(dx, dy) < DRAG_START) return;
      drag.moving = true;
      drag.pan.setPointerCapture(drag.id);
      drag.pan.setAttribute("data-dragging", "");
    }
    drag.pan.scrollLeft = drag.left - dx;
    drag.pan.scrollTop = drag.top - dy;
  }

  #endDrag() {
    const drag = this.#drag;
    if (!drag) return;
    this.#drag = null;
    if (!drag.moving) return;
    drag.pan.removeAttribute("data-dragging");
    this.#dragged = true;
  }

  #onZoom(event) {
    const input = event.target.closest("[data-lightbox-zoom]");
    if (!input) return;
    const pan = input.closest("[data-lightbox-pan]");
    if (!input.checked) {
      pan.scrollTo(0, 0);
      return;
    }
    // The clicked point stays under the pointer; from the keyboard the middle stays in the middle.
    const rect = pan.querySelector("img").getBoundingClientRect();
    const box = pan.getBoundingClientRect();
    const point = this.#point ?? { x: box.left + box.width / 2, y: box.top + box.height / 2, fx: 0.5, fy: 0.5 };
    pan.scrollBy(rect.left + point.fx * rect.width - point.x, rect.top + point.fy * rect.height - point.y);
  }

  #onKey(event) {
    // A key press means the next zoom doesn't come from a pointer.
    this.#point = null;
    // With a modifier the arrows are the browser's (Alt/Cmd+Left goes back) or a screen reader's.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
    const track = this.#track;
    if (!step || !track) return;
    // Zoomed: the arrows move the photo, a quarter of the window at a time (the row must not
    // scroll on to the next photo under it).
    const zoomed = this.querySelector("[data-lightbox-zoom]:checked");
    if (zoomed) {
      const pan = zoomed.closest("[data-lightbox-pan]");
      event.preventDefault();
      pan.scrollBy({ left: (step[0] * pan.clientWidth) / 4, top: (step[1] * pan.clientHeight) / 4, behavior: reducedMotion.matches ? "instant" : "smooth" });
      return;
    }
    if (!step[0]) return;
    const slide = track.children[Math.round(Math.abs(track.scrollLeft) / track.clientWidth)];
    const link = slide?.querySelector(`[data-lightbox-nav="${step[0] < 0 ? "previous" : "next"}"]`);
    if (!link) return;
    event.preventDefault();
    link.click();
  }

  #zoomOut() {
    for (const input of this.querySelectorAll("[data-lightbox-zoom]:checked")) {
      input.checked = false;
      input.closest("[data-lightbox-pan]").scrollTo(0, 0);
    }
  }
}

if (!customElements.get("product-lightbox")) customElements.define("product-lightbox", ProductLightbox);
